// Les employeurs de Radar en mots (lot 3 de la refonte). Un seul endroit dit
// l'état d'une page suivie ou demandée, pour que l'accueil et la page
// « Employeurs » ne racontent jamais deux histoires sur le même employeur.

import { ChevronRight, CircleAlert, Clock } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { ScoreRing, mouvementReduit, scoreTone } from '@ai-job-fit/ui'
import { useT } from '../lib/i18n'
import { fullDate, parseDate } from '../lib/format'
import { LogoEntreprise } from './sites'
import type { FiltreAbonnement } from '../../services/filtreOffre'

export interface LigneEmployeur {
  id: number
  nom: string
  logo: string | null
  annonces: boolean
  dansMetiers: number
  meilleure: number | null
  auDessus: number
  seuil: number
  derniere: string | null
  enErreur: boolean
  erreurDepuis: string | null
  lu: string | null
  /** Le filtre posé sur ce site (issue #191), absent ou `null` : tout. */
  filtre?: FiltreAbonnement | null
  /** Des offres de sa zone attendent encore leur lecture (issue #201). */
  enLecture?: boolean
}

export interface PageEnAttente {
  id: number
  url: string
  nom: string
  statut: 'en_attente' | 'refusee'
  demandeeLe: string
  reponseAvant: string | null
  gardees: number
  meilleure: number | null
}

export interface Pont {
  nom: string
  gardees: number
  sourceId: number | null
}

const JOUR = 86_400_000

/** Combien de jours entiers depuis une date SQLite, 0 si inconnue. */
function joursDepuis(date: string | null): number {
  const d = parseDate(date)
  return d ? Math.max(0, Math.floor((Date.now() - d.getTime()) / JOUR)) : 0
}

/** L'état d'une page suivie, en une phrase, et son ton. */
export function useEtatEmployeur(): (e: LigneEmployeur) => { texte: string; ton: '' | 'calme' | 'err' } {
  const t = useT()
  return (e) => {
    if (e.enErreur) {
      const nuits = joursDepuis(e.erreurDepuis) + 1
      return { texte: nuits > 1 ? t('emp.illisibleNuits', { n: nuits }) : t('emp.illisibleNuit'), ton: 'err' }
    }
    // Dans vos métiers : combien passent VOTRE seuil d'abord. « 10 offres
    // dans vos métiers » seul se lisait « 10 offres pour moi », alors que
    // huit étaient sous 50 et n'apparaissaient nulle part.
    if (e.dansMetiers > 0) {
      const m = e.meilleure ?? 0
      const phrase =
        e.auDessus === 0
          ? e.dansMetiers === 1
            ? t('emp.sousSeuilUn', { m, seuil: e.seuil })
            : t('emp.sousSeuil', { n: e.dansMetiers, m, seuil: e.seuil })
          : e.dansMetiers === 1
            ? t('emp.uneSeule', { m })
            : e.auDessus === 1
              ? t('emp.pourVousUn', { n: e.dansMetiers, m })
              : t('emp.pourVousPlus', { k: e.auDessus, n: e.dansMetiers, m })
      return { texte: e.annonces ? t('emp.prefixeAnnonces', { phrase }) : phrase, ton: e.auDessus > 0 ? '' : 'calme' }
    }
    // Pas encore lu n'est pas calme : un gros site se lit 400 offres par nuit,
    // les plus récentes d'abord (issue #201).
    if (e.enLecture) return { texte: t('emp.enLecture'), ton: 'calme' }
    if (e.annonces) return { texte: t('accueil2.empAnnoncesRien'), ton: 'calme' }
    // Calme : dire depuis quand, si on le sait et que ça fait au moins deux
    // semaines. « Calme » tout court ne dit pas si la page vit encore.
    const semaines = Math.floor(joursDepuis(e.derniere) / 7)
    return { texte: e.derniere && semaines >= 2 ? t('emp.calmeDepuis', { n: semaines }) : t('accueil2.empCalme'), ton: 'calme' }
  }
}

/** L'état d'une page demandée et pas encore lue. `court` : la ligne de
 *  l'accueil ; sinon la carte de la page « Employeurs ». */
export function useEtatPage(max: number): (p: PageEnAttente, court: boolean) => string {
  const t = useT()
  return (p, court) => {
    const hote = p.url.replace(/^https?:\/\//, '').replace(/\/$/, '')
    if (p.statut === 'refusee') return t('emp.refusee', { hote })
    const enRetard = !p.reponseAvant || (parseDate(p.reponseAvant)?.getTime() ?? 0) < Date.now()
    if (court) {
      return enRetard
        ? t('emp.apprentissageDepuis', { date: fullDate(p.demandeeLe), max })
        : t('emp.apprentissage', { date: fullDate(p.reponseAvant), max })
    }
    return enRetard
      ? t('emp.apprendLireDepuis', { hote, date: fullDate(p.demandeeLe) })
      : t('emp.apprendLire', { hote, date: fullDate(p.reponseAvant) })
  }
}

// L'employeur qu'on vient de suivre (#187) : sa ligne arrive dans « Suivis »
// et s'y éclaire un instant, pour qu'on voie où il est allé. La note est
// consommée par la ligne qui en naît : un retour sur la page ne rejoue rien.
let dernierSuivi: { id: number; at: number } | null = null
export function noterSuivi(id: number): void {
  dernierSuivi = { id, at: Date.now() }
}

/** Une ligne d'employeur suivi, cliquable. */

export function LigneEmp({ e, onClick, droite }: { e: LigneEmployeur; onClick?: () => void; droite?: React.ReactNode }) {
  const etat = useEtatEmployeur()(e)
  const neDuSuivi = useRef(!!dernierSuivi && dernierSuivi.id === e.id && Date.now() - dernierSuivi.at < 30000)
  const [vient] = useState(() => neDuSuivi.current && !mouvementReduit())
  useEffect(() => {
    if (neDuSuivi.current) { dernierSuivi = null; neDuSuivi.current = false }
  }, [])
  const contenu = (
    <>
      <LogoEntreprise nom={e.nom} logo={e.logo} className="pc-logo acc-logo-sm" />
      <span className="grow">
        <b>{e.nom}</b>
        <span className={`acc-etat${etat.ton ? ` ${etat.ton}` : ''}`}>
          {etat.ton === 'err' && <CircleAlert size={12} />}
          {etat.texte}
        </span>
      </span>
      {droite}
      {onClick && <ChevronRight size={16} className="acc-chev" />}
    </>
  )
  return onClick ? (
    <button className={`acc-ligne acc-lien${vient ? ' vient' : ''}`} onClick={onClick}>{contenu}</button>
  ) : (
    <div className={`acc-ligne${vient ? ' vient' : ''}`}>{contenu}</div>
  )
}

/** Une page en apprentissage ou refusée : un employeur nommé, pas une adresse. */
export function LignePage({ p, max, court, onClick }: { p: PageEnAttente; max: number; court: boolean; onClick?: () => void }) {
  const t = useT()
  const etat = useEtatPage(max)(p, court)
  const contenu = (
    <>
      <LogoEntreprise nom={p.nom} logo={null} className="pc-logo acc-logo-sm" />
      <span className="grow">
        <b>{p.nom}</b>
        <span className={`acc-etat ${p.statut === 'refusee' ? 'calme' : 'att'}`}>
          {p.statut === 'en_attente' && <Clock size={12} />}
          {etat}
        </span>
        {!court && p.gardees > 0 && (
          <span className="acc-etat calme">
            {t(p.statut === 'refusee' ? 'emp.rangeesIciRefus' : 'emp.rangeesIci', { nom: p.nom })}
          </span>
        )}
      </span>
      {!court && p.meilleure !== null && <ScoreRing score={p.meilleure} tone={scoreTone(p.meilleure)} size={34} />}
      {onClick && <ChevronRight size={16} className="acc-chev" />}
    </>
  )
  return onClick ? (
    <button className="acc-ligne acc-lien" onClick={onClick}>{contenu}</button>
  ) : (
    <div className="acc-ligne">{contenu}</div>
  )
}
